import type { TFunction } from "i18next";
import type { DefinitionTooltipInfo } from "src/components/common";
import type { NotificationSystem } from "../Types/NotificationTypes";

/**
 * Help text for the Notification Preferences activity rows, stored under
 * `notificationPreferences.metricHelp.<system>.<slug>`.
 *
 * Rows are built from the settings API, so the only identifiers available on the
 * client are `section.system` and `item.key` / `item.label`. Rather than depend
 * on one of those, both are normalized to the same slug and checked — a row
 * resolves whether the backend sends `total_sales`, `Total Sales`, or an alias
 * listed below.
 */

// `String(… ?? "")` because the key and label come off the API — the response
// type says `string`, but a missing value must not throw here.
const slugify = (value: string) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

/**
 * Backend metric keys that don't slugify to their own copy key. Extend this as
 * new metrics appear rather than duplicating the copy itself.
 */
const KEY_ALIASES: Partial<Record<NotificationSystem, Record<string, string>>> = {
  sales: {
    sales: "total_sales",
    revenue: "total_sales",
    total_revenue: "total_sales",
    revenue_growth: "total_sales_growth",
    sales_growth: "total_sales_growth",
    returns: "return_rate",
  },
  paid_media: {
    spend: "marketing_spend",
    ad_spend: "marketing_spend",
    cpp: "cost_per_purchase",
    cpa: "cost_per_acquisition",
    purchase_roas: "roas",
    impressions: "paid_impressions",
    clicks: "paid_clicks",
    engagements: "paid_engagements",
    views: "paid_views",
    campaign_summary: "ad_campaign_performance_summary",
    ad_campaign_summary: "ad_campaign_performance_summary",
    campaign_performance_summary: "ad_campaign_performance_summary",
  },
  social: {
    follower_growth: "follower_growth_trend",
    follower_trend: "follower_growth_trend",
    top_post: "top_performing_post",
    best_post: "top_performing_post",
  },
  web_analytics: {
    top_pages: "top_5_most_viewed_pages",
    most_viewed_pages: "top_5_most_viewed_pages",
    top_5_pages: "top_5_most_viewed_pages",
    avg_session_duration: "session_duration",
    average_session_duration: "session_duration",
    sessions: "website_traffic",
    traffic: "website_traffic",
    visits: "website_traffic",
  },
};

const resolveSlug = (system: NotificationSystem, candidate: string) => {
  const slug = slugify(candidate);
  if (!slug) {
    return "";
  }
  return KEY_ALIASES[system]?.[slug] ?? slug;
};

/**
 * Returns the tooltip for an activity row, or `null` when the metric has no
 * copy yet — callers should skip rendering the (?) trigger in that case rather
 * than showing an empty popover.
 */
export const getNotificationMetricHelp = (
  t: TFunction,
  system: NotificationSystem,
  key: string,
  label: string
): DefinitionTooltipInfo | null => {
  for (const candidate of [key, label]) {
    const slug = resolveSlug(system, candidate);
    if (!slug) {
      continue;
    }

    const base = `notificationPreferences.metricHelp.${system}.${slug}`;
    const description = t(`${base}.description`, { defaultValue: "" });
    if (!description) {
      continue;
    }

    const formula = t(`${base}.formula`, { defaultValue: "" });
    const note = t(`${base}.note`, { defaultValue: "" });
    return {
      description,
      ...(formula ? { formula } : {}),
      ...(note ? { note } : {}),
    };
  }

  return null;
};
